import {
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import {
  localLogin,
  verifyLocalLogin,
  logout,
} from 'src/api/users';
import {
  getItem,
  setItem,
  clearAllLS,
} from 'src/utils/localStorage';

export interface AuthState {
  id: string;
  token: string;
  status: 'idle' | 'loading' | 'ready';
  tempUserInfo?: {
    verifyToken: string;
    userDepartmentRoles: any[];
  } | null;
  user?: any;
}

const initialState: AuthState = {
  id: '',
  token: '',
  status: 'idle',
  tempUserInfo: null,
  user: null,
};

export const loginStep1 = createAsyncThunk(
  'auth/loginStep1',
  async (loginData: any, { rejectWithValue }) => {
    try {
      const { data } = await localLogin(loginData);
      return data;
    } catch (error) {
      const err = error as any;
      return rejectWithValue(
        err.response?.data || err.message,
      );
    }
  },
);

export const loginStep2 = createAsyncThunk(
  'auth/loginStep2',
  async (verifyData: any, { rejectWithValue }) => {
    try {
      const verifyPayload = {
        verifyToken: verifyData?.verifyToken,
        depId: verifyData?.depId,
        roleId: verifyData?.roleId,
      };
      const { data } = await verifyLocalLogin(
        verifyPayload,
      );

      const selectedDeptId = String(
        verifyData?.depId || '',
      ).trim();
      const selectedRoleId = String(
        verifyData?.roleId || '',
      ).trim();
      const selectedDeptName = String(
        verifyData?.deptName || '',
      ).trim();
      const selectedRoleName = String(
        verifyData?.roleName || '',
      ).trim();
      const selectedLeadershipDeptIds = Array.isArray(
        verifyData?.leadershipDeptIds,
      )
        ? verifyData.leadershipDeptIds.filter(
            (item: any): item is string =>
              typeof item === 'string' &&
              item.trim().length > 0,
          )
        : typeof verifyData?.leadershipDeptIds ===
            'string' &&
          verifyData.leadershipDeptIds.trim().length > 0
        ? [verifyData.leadershipDeptIds.trim()]
        : [];
      const responseUser = data?.user || {};
      const mergedUser = {
        ...responseUser,
      } as any;

      if (selectedDeptId) {
        mergedUser.deptId = selectedDeptId;
      }
      if (selectedRoleId) {
        mergedUser.roleId = selectedRoleId;
      }
      if (selectedDeptName) {
        mergedUser.deptName = selectedDeptName;
      }
      if (selectedRoleName) {
        mergedUser.roleName = selectedRoleName;
      }
      if (selectedLeadershipDeptIds.length > 0) {
        mergedUser.leadershipDeptIds =
          selectedLeadershipDeptIds;
      }
      if (!mergedUser.userType && selectedRoleId) {
        mergedUser.userType = selectedRoleId;
      }

      localStorage.setItem('ework_token', data.accessToken);
      localStorage.setItem(
        'ework_refresh_token',
        data.refreshToken,
      );
      localStorage.setItem(
        'eworkTokenData',
        data.accessToken,
      );

      // Construct tokenData object for localStorage compatible with existing request utility
      const tokenData = {
        id_token: data.accessToken,
        refresh_token: data.refreshToken,
        sessionId: 'local-session',
        ...mergedUser,
      };
      setItem('tokenData', JSON.stringify(tokenData));
      setItem('ework_user_id', mergedUser.id);
      return {
        ...data,
        user: mergedUser,
      };
    } catch (error) {
      const err = error as any;
      return rejectWithValue(
        err.response?.data || err.message,
      );
    }
  },
);

export const userLogout = createAsyncThunk(
  'auth/logout',
  async () => {
    const firebaseToken = getItem('ework_firebase_token');

    const _logout = () => {
      clearAllLS();
      window.location.href = `${process.env.PUBLIC_URL}/login`;
    };

    if (firebaseToken) {
      logout({
        token: firebaseToken,
      }).then(
        res => {
          _logout();
        },
        error => {
          _logout();
        },
      );
    } else {
      _logout();
    }
  },
);

export const authenSlice = createSlice({
  name: 'authen',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(
      loginStep1.fulfilled,
      (state, action: any) => {
        state.tempUserInfo = action.payload;
        state.status = 'idle';
      },
    );
    builder.addCase(
      loginStep2.fulfilled,
      (state, action: any) => {
        const { accessToken, user } = action.payload;
        state.token = accessToken;
        state.id = user.id;
        state.user = user;
        state.status = 'ready';
        state.tempUserInfo = null;
      },
    );
  },
});

export const selectAuthen = (state: RootState) =>
  state.authen;

export default authenSlice.reducer;
